<template>
  <div class="statistics-page">
    <div class="page-header">
      <h2 class="page-title">资产统计分析</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">统计年度</span>
        <el-date-picker
          v-model="year"
          type="year"
          size="small"
          placeholder="选择年度"
          value-format="yyyy"
          style="width: 120px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">资产类型</span>
        <el-select v-model="assetType" size="small" style="width: 140px">
          <el-option v-for="item in assetTypes" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row">
      <stat-card
        title="配额持有总量"
        :today="summary.quotaTotal.today"
        :annual="summary.quotaTotal.annual"
        :unit="summary.quotaTotal.unit"
        :annual-unit="summary.quotaTotal.unit"
        icon="el-icon-coin"
        color="#409EFF"
      />
      <stat-card
        title="CCER 持有量"
        :today="summary.ccerTotal.today"
        :annual="summary.ccerTotal.annual"
        :unit="summary.ccerTotal.unit"
        :annual-unit="summary.ccerTotal.unit"
        icon="el-icon-medal"
        color="#67C23A"
      />
      <stat-card
        title="资产总价值"
        :today="summary.totalValue.today"
        :annual="summary.totalValue.annual"
        :unit="summary.totalValue.unit"
        :annual-unit="summary.totalValue.unit"
        icon="el-icon-money"
        color="#1a3a5c"
      />
      <stat-card
        title="较上年变化"
        :today="summary.yoyChange.today"
        :annual="summary.yoyChange.annual"
        :unit="summary.yoyChange.unit"
        :annual-unit="summary.yoyChange.unit"
        icon="el-icon-top"
        color="#E6A23C"
      />
    </div>

    <div class="chart-section">
      <chart-card title="资产结构占比" class="share-chart">
        <base-chart :option="shareChartOption" />
      </chart-card>
      <chart-card title="月度持有量趋势" class="trend-chart">
        <base-chart :option="trendChartOption" />
      </chart-card>
    </div>

    <div class="table-section">
      <el-table :data="categorySummary" border stripe size="small">
        <el-table-column prop="assetType" label="资产类型" width="140" />
        <el-table-column prop="quantity" label="持有数量 (tCO2)" min-width="160" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.quantity, 0) }}</template>
        </el-table-column>
        <el-table-column prop="ratio" label="占比 (%)" width="120" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.ratio, 2) }}</template>
        </el-table-column>
        <el-table-column prop="available" label="可用量 (tCO2)" min-width="140" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.available, 0) }}</template>
        </el-table-column>
        <el-table-column prop="value" label="估值 (万元)" min-width="130" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.value, 2) }}</template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getStatisticsData } from '@/api/modules/carbonAsset'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonAssetStatistics',
  components: { FilterBar, StatCard, ChartCard, BaseChart },
  data() {
    return {
      year: '2025',
      assetType: '全部',
      assetTypes: [],
      summary: {
        quotaTotal: { today: 0, annual: 0, unit: 'tCO2' },
        ccerTotal: { today: 0, annual: 0, unit: 'tCO2' },
        totalValue: { today: 0, annual: 0, unit: '万元' },
        yoyChange: { today: 0, annual: 0, unit: '%' }
      },
      categorySummary: [],
      shareChartOption: {},
      trendChartOption: {}
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getStatisticsData({
        year: this.year,
        assetType: this.assetType === '全部' ? '' : this.assetType
      })
      const data = res.data
      this.assetTypes = data.assetTypes
      this.summary = data.summary
      this.categorySummary = data.categorySummary
      this.buildShareChart(data.typeShare)
      this.buildTrendChart(data.monthlyTrend)
    },
    buildShareChart(share) {
      this.shareChartOption = {
        tooltip: { trigger: 'item', formatter: '{b}: {c} tCO2 ({d}%)' },
        legend: { orient: 'vertical', right: 10, top: 'center' },
        color: ['#409EFF', '#67C23A', '#E6A23C'],
        series: [{
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['40%', '50%'],
          data: share,
          label: { formatter: '{b}\n{d}%' }
        }]
      }
    },
    buildTrendChart(trend) {
      this.trendChartOption = {
        tooltip: { trigger: 'axis' },
        grid: { left: 60, right: 30, top: 30, bottom: 40 },
        xAxis: {
          type: 'category',
          data: trend.months.map(m => m.replace(`${this.year}-`, ''))
        },
        yAxis: {
          type: 'value',
          name: 'tCO2',
          axisLabel: {
            formatter: val => (val / 10000) + '万'
          }
        },
        series: [{
          name: '持有量',
          type: 'line',
          data: trend.holdings,
          smooth: true,
          symbol: 'circle',
          lineStyle: { color: '#409EFF', width: 2 },
          itemStyle: { color: '#409EFF' },
          areaStyle: { color: 'rgba(64, 158, 255, 0.12)' }
        }]
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.chart-section {
  display: flex;
  gap: 16px;
  height: 360px;
  margin-bottom: 16px;

  .share-chart,
  .trend-chart {
    flex: 1;
  }
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  overflow-x: auto;
}
</style>
